import type { ShipmentStatusConfig, ShipmentStatus } from '../../../domain/types/configs/company/ShipmentStatusConfig';

// Estados compartidos para AIR y SEA (8 estados)
const airSeaStatuses: ShipmentStatus[] = [
  {
    order: 0,
    trigger: "booking",
    type: "string",
    label: "Booking Request",
    id: "BOOKING_REQUEST",
    visible: true
  },
  {
    order: 1,
    label: "Picked Up",
    id: "PICKED_UP",
    trigger: "milestones.pickupDate",
    type: "string",
    visible: true
  },
  {
    label: "Departed",
    order: 2,
    trigger: "milestones.atd",
    id: "DEPARTED",
    type: "string",
    visible: true
  },
  {
    order: 3,
    label: "Arrived",
    id: "ARRIVED",
    type: "string",
    trigger: "milestones.ata",
    visible: true
  },
  {
    type: "bool",
    id: "CUSTOMS",
    order: 4,
    trigger: "customsFormalitiesInitialized",
    label: "Customs",
    visible: true
  },
  {
    label: "Cleared",
    order: 5,
    id: "CLEARED",
    type: "bool",
    trigger: "customsFormalitiesCompleted",
    visible: true
  },
  {
    label: "Delivered",
    type: "string",
    order: 6,
    trigger: "milestones.deliveryDate",
    id: "DELIVERED",
    visible: true
  },
  {
    id: "COMPLETED",
    order: 7,
    type: "bool",
    label: "Completed",
    trigger: "isShipmentCompleted",
    visible: true
  }
];

// Estados para transporte terrestre OTM (16 estados)
const roaStatuses: ShipmentStatus[] = [
  {
    order: 0,
    id: "OTM_REQUEST",
    label: "Solicitud OTM",
    trigger: "otmRequestDate",
    type: "string",
    visible: true,
    description: "Recepción de la instrucción de transporte por parte del cliente",
    milestone: "Fecha solicitud OTM",
    emailIntent: "Solicitar ejecución de transporte terrestre",
    keywords: ["solicitud OTM", "solicitamos transporte", "instrucción de transporte"],
    context: "Cliente como remitente, puede no traer adjuntos"
  },
  {
    order: 1,
    id: "INITIAL_DOCS",
    label: "Docs iniciales",
    trigger: "initialDocumentsReceivedDate",
    type: "string",
    visible: true,
    description: "Recepción de documentos base de la operación (BL, factura, packing list, instrucciones)",
    milestone: "Fecha recepción documentos iniciales",
    emailIntent: "Entregar documentación inicial",
    keywords: ["adjunto BL", "factura comercial", "packing list"],
    context: "Correos con adjuntos y referencia a embarque"
  },
  {
    order: 2,
    id: "VESSEL_ARRIVAL",
    label: "Arribo nave",
    trigger: "milestones.ata",
    type: "string",
    visible: true,
    description: "Confirmación del arribo de la motonave al puerto de descarga",
    milestone: "ATA – Fecha real de arribo",
    emailIntent: "Informar llegada del buque",
    keywords: ["arribo", "ATA", "vessel arrived", "motonave arribó"],
    context: "Puede venir de naviera o forwarder"
  },
  {
    order: 3,
    id: "OTM_PRESENTED",
    label: "OTM presentado",
    trigger: "otmPresentationDate",
    type: "string",
    visible: true,
    description: "Presentación de la información y documentación del OTM ante el puerto o terminal",
    milestone: "Fecha presentación OTM",
    emailIntent: "Confirmar presentación ante puerto",
    keywords: ["OTM presentado", "presentación realizada"],
    context: "Normalmente sin adjuntos"
  },
  {
    order: 4,
    id: "OTM_RELEASED",
    label: "OTM liberado",
    trigger: "releaseDate",
    type: "string",
    visible: true,
    description: "Confirmación de la liberación del contenedor o la carga",
    milestone: "Fecha liberación",
    emailIntent: "Informar liberación",
    keywords: ["liberado", "liberación", "release"],
    context: "Puede mencionar aduana o selectividad"
  },
  {
    order: 5,
    id: "PORT_CLOSED",
    label: "Puerto cerrado",
    trigger: "portProcessCompletionDate",
    type: "string",
    visible: true,
    description: "Finalización de la gestión operativa con el puerto, sin trámites pendientes",
    milestone: "Fecha fin gestión puerto",
    emailIntent: "Confirmar fin de gestión portuaria",
    keywords: ["gestión finalizada", "sin pendientes"],
    context: "Transición a fase terrestre"
  },
  {
    order: 6,
    id: "OTM_CONFIRMED",
    label: "OTM confirmado",
    trigger: "otmConfirmationDate",
    type: "string",
    visible: true,
    description: "Aprobación interna para ejecutar la operación terrestre",
    milestone: "Fecha confirmación OTM",
    emailIntent: "Confirmar que el OTM puede ejecutarse",
    keywords: ["OTM confirmado", "aprobado"],
    context: "Normalmente correo interno"
  },
  {
    order: 7,
    id: "PICKUP_APPOINTMENT",
    label: "Cita cargue",
    trigger: "pickupAppointmentDate",
    type: "string",
    visible: true,
    description: "Confirmación de fecha y hora para el retiro o cargue",
    milestone: "Fecha y hora cita de cargue",
    emailIntent: "Informar cita asignada",
    keywords: ["cita de cargue", "pickup appointment"],
    context: "Incluye fecha y hora explícitas"
  },
  {
    order: 8,
    id: "PORT_PICKUP",
    label: "Retiro puerto",
    trigger: "containerPickupDate",
    type: "string",
    visible: true,
    description: "Retiro del contenedor desde el puerto o terminal",
    milestone: "Fecha retiro contenedor",
    emailIntent: "Confirmar retiro",
    keywords: ["retirado", "pickup realizado"],
    context: "Puede incluir placa o conductor"
  },
  {
    order: 9,
    id: "IN_TRANSIT",
    label: "En tránsito",
    trigger: "transitStartDate",
    type: "string",
    visible: true,
    description: "Inicio del transporte terrestre hacia el destino",
    milestone: "Fecha inicio tránsito",
    emailIntent: "Informar que el vehículo va en ruta",
    keywords: ["en tránsito", "en ruta"],
    context: "Puede venir de transportista"
  },
  {
    order: 10,
    id: "ARRIVED_DESTINATION",
    label: "Llegada destino",
    trigger: "arrivalAtDestinationDate",
    type: "string",
    visible: true,
    description: "Llegada del vehículo al punto de entrega o depósito",
    milestone: "Fecha llegada a destino",
    emailIntent: "Confirmar llegada",
    keywords: ["llegó a planta", "arrival at destination"],
    context: "Previo al descargue"
  },
  {
    order: 11,
    id: "CARGO_UNLOADED",
    label: "Mercancía descargada",
    trigger: "unloadingCompletionDate",
    type: "string",
    visible: true,
    description: "Descargue de la mercancía en el punto de destino",
    milestone: "Fecha descargue mercancía",
    emailIntent: "Confirmar descargue",
    keywords: ["descargue realizado", "unloading completed"],
    context: "Puede traer remisión firmada"
  },
  {
    order: 12,
    id: "EMPTY_IN_TRANSIT",
    label: "Vacío en tránsito",
    trigger: "emptyContainerTransitStartDate",
    type: "string",
    visible: true,
    description: "Traslado del contenedor vacío hacia el patio de devolución",
    milestone: "Fecha salida unidad vacía",
    emailIntent: "Informar salida del vacío",
    keywords: ["unidad vacía en tránsito"],
    context: "Posterior al descargue"
  },
  {
    order: 13,
    id: "EMPTY_RETURNED",
    label: "Vacío entregado",
    trigger: "emptyContainerReturnDate",
    type: "string",
    visible: true,
    description: "Entrega y devolución del contenedor vacío",
    milestone: "Fecha devolución unidad vacía",
    emailIntent: "Confirmar devolución",
    keywords: ["vacío entregado", "devolución contenedor"],
    context: "Puede venir de transportista o naviera"
  },
  {
    order: 14,
    id: "FINAL_DOCS",
    label: "Docs finales",
    trigger: "finalDocumentsReceivedDate",
    type: "string",
    visible: true,
    description: "Recepción de documentos finales y soportes de la operación",
    milestone: "Fecha recepción documentos finales",
    emailIntent: "Entregar soportes finales",
    keywords: ["remisión firmada", "documentos finales"],
    context: "Adjuntos post-operación"
  },
  {
    order: 15,
    id: "OPERATION_CLOSED",
    label: "Operación cerrada",
    trigger: "operationClosureDate",
    type: "string",
    visible: true,
    description: "Cierre definitivo de la operación por parte del usuario",
    milestone: "Fecha cierre operación",
    emailIntent: "Confirmar cierre de la operación",
    keywords: ["operación cerrada", "cierre operativo"],
    context: "Debe ser marcado manualmente"
  }
];

export const shipmentStatusConfig: ShipmentStatusConfig = {
  // Nueva estructura por modo de transporte
  statusByMode: {
    AIR: airSeaStatuses,
    SEA: airSeaStatuses,
    ROA: roaStatuses
  },
  
  // Mantener estructura antigua para compatibilidad
  status: airSeaStatuses
}; 